import React from 'react';
import { Mail, Phone, MapPin, ArrowUp } from 'lucide-react';
import { companyInfo, offices } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';

const EnsoCircle = ({ className = '' }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M85 50C85 69.33 69.33 85 50 85C30.67 85 15 69.33 15 50C15 30.67 30.67 15 50 15C62 15 72.5 21 79 30"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
  </svg>
);

export default function Footer() {
  const { t, tl } = useLanguage();

  const navLinks = [
    { labelKey: 'nav_about', href: '#about' },
    { labelKey: 'nav_services', href: '#services' },
    { labelKey: 'nav_team', href: '#team' },
    { labelKey: 'nav_portfolio', href: '#portfolio' },
    { labelKey: 'nav_contact', href: '#contact' },
  ];

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#060608] border-t border-[#1a1a1f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <EnsoCircle className="w-8 h-8 text-[#c9a96e]" />
              <span className="text-[#f0ebe3] font-heading text-lg font-bold tracking-[0.15em]">
                ENSO
              </span>
            </div>
            <p className="text-[#6a6560] text-sm leading-relaxed mb-4">
              {t('footer_tagline')} {companyInfo.registrationNumber}.
            </p>
            <p className="text-[#6a6560] text-xs">{t('footer_attestation')}</p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-[#c9a96e] text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              {t('footer_nav')}
            </h4>
            <nav className="flex flex-col gap-2.5">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollTo(e, link.href)}
                  className="text-[#8a857e] hover:text-[#f0ebe3] text-sm"
                  style={{ transition: 'color 0.3s ease' }}
                >
                  {t(link.labelKey)}
                </a>
              ))}
            </nav>
          </div>

          {/* Offices */}
          <div>
            <h4 className="text-[#c9a96e] text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              {t('footer_offices')}
            </h4>
            <div className="flex flex-col gap-2.5">
              {offices.map((office) => (
                <div key={office.city} className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#c9a96e] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-[#f0ebe3] text-sm">{office.city}</span>
                    <span className="text-[#6a6560] text-xs ml-2">
                      {office.note ? `(${tl(office.note)})` : tl(office.type)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[#c9a96e] text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              {t('footer_contact')}
            </h4>
            <div className="flex flex-col gap-3">
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-[#8a857e] hover:text-[#c9a96e] text-sm" style={{ transition: 'color 0.3s ease' }}>
                <Mail className="w-4 h-4" />
                {companyInfo.email}
              </a>
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-[#8a857e] hover:text-[#c9a96e] text-sm" style={{ transition: 'color 0.3s ease' }}>
                <Phone className="w-4 h-4" />
                {companyInfo.phone}
              </a>
              <div className="flex items-start gap-3 text-[#6a6560] text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {companyInfo.address}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-[#1a1a1f] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#4a4540] text-xs">
            &copy; {new Date().getFullYear()} ENSO SPRL. {t('footer_rights')}
          </p>
          <button onClick={scrollToTop} className="flex items-center gap-2 text-[#6a6560] hover:text-[#c9a96e] text-xs tracking-wider uppercase" style={{ transition: 'color 0.3s ease' }}>
            {t('footer_back_top')}
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </footer>
  );
}
